import { useEffect, useRef, useState } from "react";
import { api, API } from "@/context/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Wrench, Sparkles, Trash2, CheckCircle2, Paperclip, Upload, FileText, X } from "lucide-react";
import { toast } from "sonner";

const KIND_ICON = { CLEANING: Sparkles, MAINTENANCE: Wrench };
const KIND_COLOR = { CLEANING: "#0891B2", MAINTENANCE: "#B45309" };

const emptyTask = {
  kind: "CLEANING",
  property_booked: "",
  start_date: "",
  end_date: "",
  notes: "",
  status: "PENDING",
  assigned_to: "",
  attachments: [],
};

const fmtSize = (b) => {
  if (!b) return "0 B";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};

export function TaskDialog({ open, onOpenChange, initial, properties = [], onSaved }) {
  const [form, setForm] = useState(emptyTask);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    const src = initial || emptyTask;
    setForm({
      ...emptyTask,
      ...src,
      // Support legacy scheduled_date
      start_date: src.start_date || src.scheduled_date || "",
      end_date: src.end_date || src.start_date || src.scheduled_date || "",
      attachments: src.attachments || [],
    });
  }, [initial, open]);

  const submit = async () => {
    if (!form.start_date) return toast.error("Pick a start date");
    if (form.end_date && form.end_date < form.start_date) return toast.error("End date must be after start date");
    setSaving(true);
    try {
      const payload = { ...form, end_date: form.end_date || form.start_date };
      let saved;
      if (form.id) {
        const r = await api.put(`/tasks/${form.id}`, payload);
        saved = r.data;
        toast.success("Task updated");
      } else {
        const r = await api.post("/tasks", payload);
        saved = r.data;
        toast.success("Task scheduled");
      }
      setForm((f) => ({ ...f, ...saved }));
      onOpenChange(false);
      onSaved?.();
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!form.id) return;
    if (!confirm("Delete this task?")) return;
    await api.delete(`/tasks/${form.id}`);
    toast.success("Deleted");
    onOpenChange(false);
    onSaved?.();
  };

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadFile = async (file) => {
    if (!form.id) {
      toast.error("Save the task first, then attach files");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/tasks/${form.id}/attachments`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, attachments: [...(f.attachments || []), r.data] }));
      toast.success("Uploaded");
      onSaved?.();
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const removeAttachment = async (att) => {
    if (!confirm("Remove this file?")) return;
    await api.delete(`/tasks/${form.id}/attachments`, { params: { url: att.url } });
    setForm((f) => ({ ...f, attachments: (f.attachments || []).filter((a) => a.url !== att.url) }));
    toast.success("File removed");
    onSaved?.();
  };

  const downloadAttachment = async (att) => {
    try {
      // att.url starts with "/api/..."; strip leading "/api" since axios baseURL already includes it
      const relUrl = att.url.startsWith("/api/") ? att.url.slice(4) : att.url;
      const r = await api.get(relUrl, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = att.filename || "download";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      toast.error("Download failed");
    }
  };

  const backendBase = API.replace(/\/api$/, "");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-lg" data-testid="task-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{form.id ? "Edit task" : "Schedule task"}</DialogTitle>
          <DialogDescription>
            Cleaning &amp; maintenance jobs appear on the calendar. They are not check-ins.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="label-eyebrow">Type</Label>
            <Select value={form.kind} onValueChange={(v) => upd("kind", v)}>
              <SelectTrigger data-testid="task-kind"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="CLEANING">Cleaning</SelectItem>
                <SelectItem value="MAINTENANCE">Maintenance</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="label-eyebrow">Status</Label>
            <Select value={form.status} onValueChange={(v) => upd("status", v)}>
              <SelectTrigger data-testid="task-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="PENDING">Pending</SelectItem>
                <SelectItem value="DONE">Done</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label className="label-eyebrow">Property</Label>
            <Select value={form.property_booked || ""} onValueChange={(v) => upd("property_booked", v)}>
              <SelectTrigger data-testid="task-property"><SelectValue placeholder="Select property" /></SelectTrigger>
              <SelectContent>
                {properties.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="label-eyebrow">Begin date</Label>
            <Input
              type="date"
              value={form.start_date || ""}
              onChange={(e) => upd("start_date", e.target.value)}
              data-testid="task-start-date"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="label-eyebrow">Finish date</Label>
            <Input
              type="date"
              value={form.end_date || ""}
              onChange={(e) => upd("end_date", e.target.value)}
              data-testid="task-end-date"
            />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label className="label-eyebrow">Assigned to</Label>
            <Input
              value={form.assigned_to || ""}
              onChange={(e) => upd("assigned_to", e.target.value)}
              placeholder="e.g. Cleaning team"
              data-testid="task-assigned"
            />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label className="label-eyebrow">Notes</Label>
            <Textarea
              value={form.notes || ""}
              onChange={(e) => upd("notes", e.target.value)}
              rows={3}
              data-testid="task-notes"
            />
          </div>

          {/* Attachments */}
          <div className="space-y-2 sm:col-span-2">
            <div className="flex items-center justify-between">
              <Label className="label-eyebrow flex items-center gap-1.5">
                <Paperclip className="w-3.5 h-3.5" /> Attachments
              </Label>
              <input
                ref={fileRef}
                type="file"
                hidden
                data-testid="task-file-input"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) uploadFile(f);
                  e.target.value = "";
                }}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => fileRef.current?.click()}
                disabled={uploading || !form.id}
                className="rounded-sm border-slate-300 h-8"
                data-testid="task-upload-button"
              >
                <Upload className="w-3.5 h-3.5 mr-1.5" /> {uploading ? "Uploading…" : "Upload file"}
              </Button>
            </div>
            {!form.id && (
              <p className="text-[11px] text-slate-500">Save the task first to attach files.</p>
            )}
            {(form.attachments || []).length > 0 && (
              <ul className="space-y-1.5 border border-slate-200 rounded-sm p-2">
                {form.attachments.map((a) => (
                  <li
                    key={a.url}
                    className="flex items-center gap-2 text-sm"
                    data-testid={`task-attachment-${a.filename}`}
                  >
                    <FileText className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                    <button
                      onClick={() => downloadAttachment(a)}
                      className="text-blue-900 hover:underline truncate flex-1 text-left"
                      data-testid={`task-attachment-download-${a.filename}`}
                    >
                      {a.filename}
                    </button>
                    <span className="text-[10px] text-slate-500">{fmtSize(a.size)}</span>
                    <button
                      onClick={() => removeAttachment(a)}
                      className="text-slate-400 hover:text-rose-700"
                      data-testid={`task-attachment-remove-${a.filename}`}
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        <DialogFooter className="gap-2">
          {form.id && (
            <Button variant="outline" onClick={remove} className="rounded-sm border-rose-300 text-rose-700 hover:bg-rose-50 mr-auto" data-testid="task-delete">
              <Trash2 className="w-4 h-4 mr-2" /> Delete
            </Button>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="rounded-sm bg-blue-900 hover:bg-blue-800 text-white" data-testid="task-save">
            {saving ? "Saving…" : form.id ? "Update task" : "Schedule task"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function TaskChip({ task, onClick }) {
  const Icon = KIND_ICON[task.kind] || Sparkles;
  const done = task.status === "DONE";
  const hasAttachment = (task.attachments || []).length > 0;
  return (
    <div
      onClick={(e) => { e.stopPropagation(); onClick?.(task); }}
      className={`cursor-pointer inline-flex items-center gap-0.5 rounded-sm px-1 py-0.5 text-[9px] font-semibold border ${
        done ? "bg-slate-100 text-slate-500 border-slate-200 line-through" : "bg-white border-current"
      }`}
      style={!done ? { color: KIND_COLOR[task.kind] || "#0F172A" } : {}}
      title={`${task.kind} · ${task.property_booked || ""} · ${task.notes || ""}`}
      data-testid={`task-chip-${task.id}`}
    >
      {done ? <CheckCircle2 className="w-2.5 h-2.5" /> : <Icon className="w-2.5 h-2.5" />}
      {task.kind === "CLEANING" ? "Clean" : "Maint"}
      {hasAttachment && <Paperclip className="w-2 h-2" />}
    </div>
  );
}

export { KIND_COLOR };
